import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs, inr,
} from "@/crm/ui";

const SENSITIVE = ["pan", "gstin", "bank_account_number", "ifsc", "bank_account_name", "upi_id"];

const CONFIG = {
  Products: {
    list: "/crm/products", create: "/crm/products", testid: "products", idKey: "product_id",
    fields: [{ key: "product_name", label: "Product Name" }, { key: "product_code", label: "Code" },
      { key: "product_category", label: "Category", options: ["Business Loan", "LAP", "Personal Loan", "Working Capital", "Machinery Loan", "Vehicle Loan", "Home Loan", "Mortgage Loan", "Professional Loan", "Other"] },
      { key: "product_type", label: "Loan Type", options: ["Secured", "Unsecured"] },
      { key: "min_amount", label: "Min Amount", type: "number" }, { key: "max_amount", label: "Max Amount", type: "number" },
      { key: "min_tenure", label: "Min Tenure (months)", type: "number" }, { key: "max_tenure", label: "Max Tenure (months)", type: "number" },
      { key: "interest_rate_range", label: "Rate Range" }, { key: "processing_fee", label: "Processing Fee" },
      { key: "eligibility_notes", label: "Eligibility" }, { key: "required_documents", label: "Required Documents (comma separated)" },
      { key: "status", label: "Status", options: ["Active", "Inactive"] }],
    columns: [{ key: "product_id", label: "Product ID" }, { key: "product_name", label: "Name" },
      { key: "product_code", label: "Code" }, { key: "product_category", label: "Category" },
      { key: "product_type", label: "Loan Type" },
      { key: "min_amount", label: "Min", right: true, render: (r) => inr(r.min_amount) },
      { key: "max_amount", label: "Max", right: true, render: (r) => inr(r.max_amount) },
      { key: "tenure", label: "Tenure", render: (r) => `${r.min_tenure}-${r.max_tenure} m` },
      { key: "interest_rate_range", label: "Rate" }, { key: "processing_fee", label: "Fee" },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
  Employees: {
    list: "/crm/employees", create: "/crm/employees", testid: "employees", idKey: "employee_id",
    fields: [{ key: "employee_name", label: "Employee Name" }, { key: "employee_code", label: "Code" },
      { key: "designation", label: "Designation" }, { key: "department", label: "Department" },
      { key: "team", label: "Team" }, { key: "branch", label: "Branch" }, { key: "city", label: "City" },
      { key: "mobile", label: "Mobile" }, { key: "official_email", label: "Official Email" },
      { key: "user_email", label: "Linked User Email (login access)" },
      { key: "role", label: "Role", options: ["employee", "manager", "operations", "finance", "admin"] },
      { key: "employment_type", label: "Employment Type", options: ["Permanent", "Contract", "Trainee", "Other"] },
      { key: "reporting_manager", label: "Reporting Manager" }, { key: "pan", label: "PAN" },
      { key: "bank_account_name", label: "Bank A/c Name" },
      { key: "bank_account_number", label: "Bank Account Number" }, { key: "ifsc", label: "IFSC" },
      { key: "commission_applicable", label: "Commission Plan", options: [{ value: "true", label: "Applicable" }, { value: "false", label: "Not applicable" }] },
      { key: "incentive_applicable", label: "Incentive Plan", options: [{ value: "true", label: "Applicable" }, { value: "false", label: "Not applicable" }] },
      { key: "monthly_target", label: "Monthly Target", type: "number" },
      { key: "status", label: "Status", options: ["Active", "Inactive"] }],
    columns: [{ key: "employee_id", label: "Employee ID" }, { key: "employee_code", label: "Code" },
      { key: "employee_name", label: "Name" }, { key: "mobile", label: "Mobile" },
      { key: "designation", label: "Designation" }, { key: "department", label: "Department" },
      { key: "reporting_manager", label: "Reports To" }, { key: "branch", label: "Branch" },
      { key: "city", label: "City" }, { key: "pan", label: "PAN" },
      { key: "bank_account_number", label: "Bank A/c" },
      { key: "monthly_target", label: "Target", right: true, render: (r) => inr(r.monthly_target) },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
  Partners: {
    list: "/crm/partners", create: "/crm/partners", testid: "partners", idKey: "partner_id",
    fields: [{ key: "partner_name", label: "Partner Name" },
      { key: "partner_type", label: "Partner Type", options: ["DSA", "Connector", "Referral Partner", "Channel Partner", "Corporate Partner", "Other"] },
      { key: "company_name", label: "Company / Firm" }, { key: "contact_person", label: "Contact Person" },
      { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
      { key: "pan", label: "PAN" }, { key: "gstin", label: "GSTIN" }, { key: "address", label: "Address" },
      { key: "city", label: "City" }, { key: "state", label: "State" }, { key: "pincode", label: "Pincode" },
      { key: "bank_account_name", label: "Bank A/c Name" },
      { key: "bank_account_number", label: "Bank Account Number" }, { key: "ifsc", label: "IFSC" },
      { key: "agreement_number", label: "Agreement Number" },
      { key: "agreement_start", label: "Agreement Start", type: "date" },
      { key: "agreement_end", label: "Agreement End", type: "date" },
      { key: "assigned_manager", label: "Assigned Employee / Manager" },
      { key: "commission_plan", label: "Commission Plan" },
      { key: "user_email", label: "Linked User Email (portal access)" },
      { key: "status", label: "Status", options: ["Active", "Inactive"] }],
    columns: [{ key: "partner_id", label: "Partner ID" }, { key: "partner_code", label: "Code" },
      { key: "partner_type", label: "Type" }, { key: "partner_name", label: "Name" },
      { key: "company_name", label: "Company" }, { key: "contact_person", label: "Contact" },
      { key: "mobile", label: "Mobile" }, { key: "pan", label: "PAN" }, { key: "gstin", label: "GSTIN" },
      { key: "city", label: "City" }, { key: "agreement_number", label: "Agreement" },
      { key: "kyc_status", label: "KYC", render: (r) => <StatusBadge value={r.kyc_status} /> },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
  Commission: {
    list: "/crm/commission-rules", create: "/crm/commission-rules", testid: "commission",
    fields: [{ key: "user_type", label: "Applies To", options: ["employee", "partner"] },
      { key: "party_id", label: "Employee / Partner ID (blank = all)" },
      { key: "bank_id", label: "Bank ID (blank = all)" }, { key: "product_id", label: "Product ID (blank = all)" },
      { key: "commission_type", label: "Commission Type", options: ["Percentage", "Fixed", "Slab Based", "Hybrid"] },
      { key: "commission_rate", label: "Rate %", type: "number" },
      { key: "fixed_amount", label: "Fixed Amount", type: "number" },
      { key: "min_slab", label: "Min Slab", type: "number" }, { key: "max_slab", label: "Max Slab", type: "number" },
      { key: "calculation_base", label: "Calculation Base", options: ["Disbursed Amount", "Approved Amount", "Login Amount", "Net Disbursed Amount"] },
      { key: "effective_from", label: "Effective From", type: "date" }],
    columns: [{ key: "rule_id", label: "Rule ID" }, { key: "version", label: "Version", right: true },
      { key: "user_type", label: "Applies To" }, { key: "party_id", label: "Party", render: (r) => r.party_id || "All" },
      { key: "bank_id", label: "Bank", render: (r) => r.bank_id || "All" },
      { key: "product_id", label: "Product", render: (r) => r.product_id || "All" },
      { key: "commission_type", label: "Type" }, { key: "commission_rate", label: "Rate %", right: true },
      { key: "calculation_base", label: "Base" }, { key: "effective_from", label: "From" },
      { key: "effective_to", label: "To", render: (r) => r.effective_to || "—" },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
  Incentives: {
    list: "/crm/incentive-rules", create: "/crm/incentive-rules", testid: "incentives",
    fields: [{ key: "user_type", label: "Applies To", options: ["employee", "partner"] },
      { key: "party_id", label: "Employee / Partner ID (blank = all)" },
      { key: "metric", label: "Metric", options: ["Monthly Disbursement", "Number of Disbursements", "Login Count", "Approval Count", "Target Achievement"] },
      { key: "min_slab", label: "Slab From", type: "number" }, { key: "max_slab", label: "Slab To", type: "number" },
      { key: "incentive_type", label: "Incentive Type", options: ["Fixed", "Percentage"] },
      { key: "fixed_bonus", label: "Fixed Bonus", type: "number" },
      { key: "incentive_rate", label: "Rate %", type: "number" },
      { key: "bank_id", label: "Bank ID (optional)" }, { key: "product_id", label: "Product ID (optional)" },
      { key: "effective_from", label: "Effective From", type: "date" }],
    columns: [{ key: "incentive_id", label: "Incentive ID" }, { key: "user_type", label: "Applies To" },
      { key: "party_id", label: "Party", render: (r) => r.party_id || "All" },
      { key: "metric", label: "Metric" },
      { key: "min_slab", label: "From", right: true, render: (r) => inr(r.min_slab) },
      { key: "max_slab", label: "To", right: true, render: (r) => inr(r.max_slab) },
      { key: "incentive_type", label: "Type" },
      { key: "fixed_bonus", label: "Bonus", right: true, render: (r) => inr(r.fixed_bonus) },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
  Rejection: {
    list: "/crm/rejection-reasons", create: "/crm/rejection-reasons", testid: "rejection",
    idKey: "reason_id",
    fields: [{ key: "reason", label: "Rejection Reason" },
      { key: "category", label: "Category", options: ["Standard", "Credit", "Documents", "Policy", "Other"] },
      { key: "status", label: "Status", options: ["Active", "Inactive"] },
      { key: "remarks", label: "Remarks" }],
    columns: [{ key: "reason_id", label: "Reason ID" }, { key: "reason", label: "Reason" },
      { key: "category", label: "Category" }, { key: "created_by", label: "Created By" },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }],
  },
};

const PartyDocuments = ({ partyType, partyId }) => {
  const docs = useApi(`/crm/party-documents?party_type=${partyType}&party_id=${partyId}`,
                      [partyType, partyId]);
  const [form, setForm] = useState({ verification_status: "Pending" });
  const types = partyType === "employee"
    ? ["PAN", "Aadhaar", "Address Proof", "Bank Proof", "Employment Document", "Education Certificate", "Other"]
    : ["PAN", "GST", "KYC", "Agreement", "Cancelled Cheque", "Bank Proof", "Address Proof", "Other"];
  return (
    <Panel title={`Documents (${(docs.data || []).length})`} testid="party-documents-panel"
           aside={<span className="text-xs text-muted-foreground">Expiry dates raise compliance alerts</span>}>
      <QuickForm testid="party-document-form" values={form} submitLabel="Add document"
                 fields={[{ key: "document_type", label: "Document Type", options: types },
                   { key: "document_number", label: "Document Number" },
                   { key: "file_name", label: "File Name / Reference" },
                   { key: "issue_date", label: "Issue Date", type: "date" },
                   { key: "expiry_date", label: "Expiry Date", type: "date" },
                   { key: "verification_status", label: "Verification", options: ["Pending", "Verified", "Rejected", "Expired"] },
                   { key: "remarks", label: "Remarks" }]}
                 onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                 onSubmit={async () => {
                   const r = await post("/crm/party-documents",
                     { ...form, party_type: partyType, party_id: partyId }, "Document added");
                   if (r) { setForm({ verification_status: "Pending" }); docs.reload(); }
                 }} />
      <div className="mt-6">
        <DataTable testid="party-documents-table" rows={docs.data} empty="No documents uploaded yet."
                   columns={[{ key: "document_type", label: "Type" },
                     { key: "document_number", label: "Number" },
                     { key: "file_name", label: "File" },
                     { key: "expiry_date", label: "Expiry", render: (r) => `${r.expiry_date || "—"}${r.expired ? " ⚠" : ""}` },
                     { key: "verification_status", label: "Status", render: (r) => <StatusBadge value={r.verification_status} /> },
                     { key: "verified_by", label: "Verified By" },
                     { key: "actions", label: "Action", render: (r) => (
                       <span className="flex gap-1">
                         {["Verified", "Rejected", "Expired"].map((s) => (
                           <Button key={s} size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`doc-${s.toLowerCase()}-${r.document_id}`}
                                   onClick={async () => {
                                     await put(`/crm/party-documents/${r.document_id}`,
                                       { verification_status: s }, `Document ${s}`);
                                     docs.reload();
                                   }}>{s}</Button>))}
                       </span>) }]} />
      </div>
    </Panel>
  );
};

const KYC_TYPES = ["PAN", "GST", "Address Proof", "Bank Proof", "Agreement", "Cancelled Cheque", "Other"];
const KYC_STATUSES = ["Pending", "Submitted", "Verified", "Rejected", "Expired"];

const PartnerDetail = ({ partnerId, onBack }) => {
  const detail = useApi(`/crm/partners/${partnerId}`, [partnerId]);
  const users = useApi("/crm/users", []);
  const employees = useApi("/crm/employees", []);
  const [edit, setEdit] = useState({});
  const [kyc, setKyc] = useState({ document_type: "PAN", status: "Submitted" });
  const [genPw, setGenPw] = useState(null);
  const [links, setLinks] = useState(null);
  const d = detail.data;
  if (!d) return <p className="text-sm text-muted-foreground" data-testid="partner-loading">Loading partner…</p>;
  const p = d.partner;
  const link = links || { user_email: p.user_email || "", employee_id: p.employee_id || "" };

  return (
    <div className="space-y-6" data-testid="partner-detail">
      <Button variant="ghost" onClick={onBack} data-testid="back-to-partners-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> All partners
      </Button>
      <PageHead eyebrow={`Partner · ${p.partner_id}`} title={p.partner_name}
                subtitle={`${p.partner_type} · ${p.company_name || "—"} · ${p.city || "—"}`}
                actions={<><StatusBadge value={`KYC ${p.kyc_status}`} /><StatusBadge value={p.status} /></>} />
      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <Stat label="Leads" value={d.performance.leads} testid="pd-leads" />
        <Stat label="Logins" value={d.performance.login} testid="pd-login" />
        <Stat label="Disbursed" value={d.performance.disbursed} testid="pd-disbursed" />
        <Stat label="Disbursed Amount" value={inr(d.performance.disbursed_amount)} testid="pd-disbursed-amount" />
        <Stat label="Commission Earned" value={inr(d.performance.commission)} testid="pd-commission" />
      </div>

      <Panel title="Partner profile" testid="partner-profile-panel">
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          {[["Partner Code", p.partner_code], ["Contact Person", p.contact_person], ["Mobile", p.mobile],
            ["Email", p.email], ["PAN", p.pan], ["GSTIN", p.gstin], ["Address", p.address],
            ["City", p.city], ["State", p.state], ["Pincode", p.pincode],
            ["Bank A/c", p.bank_account_number], ["IFSC", p.ifsc],
            ["Agreement", p.agreement_number], ["Agreement End", p.agreement_end],
            ["Assigned Manager", p.assigned_manager], ["Commission Plan", p.commission_plan],
            ["Portal user", p.user_email]].map(([k, v]) => (
            <p key={k}><span className="text-muted-foreground">{k}: </span><b>{v || "—"}</b></p>))}
        </div>
      </Panel>

      <Panel title="Portal access" testid="partner-portal-panel"
             subtitle={p.email || p.user_email
               ? "Generate a login for this partner, then email them a secure set-password link."
               : "Add an email (or link a user) below first, then generate a login."}>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={async () => {
            const r = await post(`/crm/partners/${partnerId}/generate-password`, {}, "Password generated");
            if (r?.password) { setGenPw(r); detail.reload(); }
          }} data-testid="generate-password-btn">Generate Password</Button>
          <Button variant="outline" onClick={async () => {
            const r = await post(`/crm/partners/${partnerId}/send-portal-access`, {}, "Portal access email sent");
            if (r) detail.reload();
          }} data-testid="send-portal-access-btn">Send Portal Access</Button>
          <span className="text-xs text-muted-foreground" data-testid="portal-status">
            {p.portal_access_sent ? `Access emailed to ${p.portal_access_email || p.email}`
              : p.portal_password_set ? "Password set — not yet emailed"
              : "No password generated yet"}
          </span>
        </div>
        {genPw && (
          <div className="mt-4 rounded-lg border border-teal-300 bg-teal-50 p-4 text-sm" data-testid="generated-password-box">
            <p className="font-medium text-teal-800">Portal login created</p>
            <p className="mt-1">Username: <b>{genPw.user_email}</b></p>
            <p>Password: <b className="font-mono" data-testid="partner-generated-password">{genPw.password}</b></p>
            <p className="mt-1 text-xs text-teal-700">{genPw.note}</p>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          For security, the welcome email contains a secure "set your password" link rather than the
          password itself — share the generated password above through a secure channel.
        </p>
      </Panel>

      <Panel title="Link accounts" testid="partner-links-panel"
             subtitle="Directly connect this partner to an existing system user or employee.">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Link User (portal login)</label>
            <select data-testid="link-user-select" value={link.user_email}
                    className="mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm"
                    onChange={(e) => setLinks({ ...link, user_email: e.target.value })}>
              <option value="">— Not linked —</option>
              {(users.data || []).map((u) => (
                <option key={u.email} value={u.email}>{u.name} · {u.email} ({u.role})</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Link Employee</label>
            <select data-testid="link-employee-select" value={link.employee_id}
                    className="mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm"
                    onChange={(e) => setLinks({ ...link, employee_id: e.target.value })}>
              <option value="">— Not linked —</option>
              {(employees.data || []).map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_name} · {emp.employee_id}</option>))}
            </select>
          </div>
        </div>
        <Button className="mt-4" onClick={async () => {
          const r = await put(`/crm/partners/${partnerId}`,
            { user_email: link.user_email, employee_id: link.employee_id }, "Links saved");
          if (r) { setLinks(null); detail.reload(); }
        }} data-testid="save-links-btn">Save links</Button>
      </Panel>

      <Panel title="Edit partner" testid="edit-partner-panel">
        <QuickForm testid="edit-partner-form" values={edit} submitLabel="Save changes"
                   fields={CONFIG.Partners.fields}
                   onChange={(k, v) => setEdit((f) => ({ ...f, [k]: v }))}
                   onSubmit={async () => {
                     const r = await put(`/crm/partners/${partnerId}`, edit, "Partner updated");
                     if (r) { setEdit({}); detail.reload(); }
                   }} />
      </Panel>

      <Panel title={`KYC documents (${d.kyc_documents.length})`} testid="partner-kyc-panel"
             aside={<span className="text-xs text-muted-foreground">
               Payout approval requires verified KYC (PAN + Agreement) unless an Admin overrides
             </span>}>
        <QuickForm testid="kyc-form" values={kyc} submitLabel="Add KYC document"
                   fields={[{ key: "document_type", label: "Document Type", options: KYC_TYPES },
                     { key: "document_number", label: "Document Number" },
                     { key: "document_name", label: "Document Name / File Reference" },
                     { key: "issue_date", label: "Issue Date", type: "date" },
                     { key: "expiry_date", label: "Expiry Date", type: "date" },
                     { key: "status", label: "Status", options: KYC_STATUSES },
                     { key: "remarks", label: "Remarks" }]}
                   onChange={(k, v) => setKyc((f) => ({ ...f, [k]: v }))}
                   onSubmit={async () => {
                     const r = await post(`/crm/partners/${partnerId}/kyc-documents`, kyc, "KYC document added");
                     if (r) { setKyc({ document_type: "PAN", status: "Submitted" }); detail.reload(); }
                   }} />
        <div className="mt-6">
          <DataTable testid="kyc-table" rows={d.kyc_documents} empty="No KYC documents captured yet."
                     columns={[{ key: "document_type", label: "Document" },
                       { key: "document_number", label: "Number" },
                       { key: "document_name", label: "Name" },
                       { key: "expiry_date", label: "Expiry" },
                       { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                       { key: "verified_by", label: "Verified By" },
                       { key: "actions", label: "Action", render: (r) => (
                         <span className="flex gap-1">
                           {["Verified", "Rejected", "Expired"].map((s) => (
                             <Button key={s} size="sm" variant="outline" className="bg-white text-[10px]"
                                     data-testid={`kyc-${s.toLowerCase()}-${r.kyc_document_id}`}
                                     onClick={async () => {
                                       await put(`/crm/partners/${partnerId}/kyc-documents/${r.kyc_document_id}`,
                                         { status: s }, `KYC ${s}`);
                                       detail.reload();
                                     }}>{s}</Button>))}
                         </span>) }]} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">Overall KYC status:</span>
          {KYC_STATUSES.map((s) => (
            <Button key={s} size="sm" variant="outline" className="bg-white text-[10px]"
                    data-testid={`partner-kyc-${s.toLowerCase()}`}
                    onClick={async () => {
                      await put(`/crm/partners/${partnerId}/kyc`, { kyc_status: s }, `Partner KYC ${s}`);
                      detail.reload();
                    }}>Mark {s}</Button>))}
        </div>
      </Panel>

      <PartyDocuments partyType="partner" partyId={partnerId} />
    </div>
  );
};

const EmployeeDetail = ({ employeeId, onBack }) => {
  const detail = useApi(`/crm/employees/${employeeId}`, [employeeId]);
  const [edit, setEdit] = useState({});
  const d = detail.data;
  if (!d) return <p className="text-sm text-muted-foreground" data-testid="employee-loading">Loading employee…</p>;
  const e = d.employee;
  return (
    <div className="space-y-6" data-testid="employee-detail">
      <Button variant="ghost" onClick={onBack} data-testid="back-to-employees-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> All employees
      </Button>
      <PageHead eyebrow={`Employee · ${e.employee_id}`} title={e.employee_name}
                subtitle={`${e.designation || "—"} · ${e.department || "—"} · ${e.branch || "—"}`}
                actions={<StatusBadge value={e.status} />} />
      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <Stat label="Leads" value={d.performance.leads} testid="ed-leads" />
        <Stat label="Logins" value={d.performance.login} testid="ed-login" />
        <Stat label="Disbursed" value={d.performance.disbursed} testid="ed-disbursed" />
        <Stat label="Disbursed Amount" value={inr(d.performance.disbursed_amount)} testid="ed-disbursed-amount" />
        <Stat label="Commission Earned" value={inr(d.performance.commission)} testid="ed-commission" />
      </div>
      <Panel title="Employee profile" testid="employee-profile-panel">
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          {[["Employee Code", e.employee_code], ["Mobile", e.mobile], ["Email", e.official_email],
            ["Designation", e.designation], ["Department", e.department], ["Reporting Manager", e.reporting_manager],
            ["Branch", e.branch], ["City", e.city], ["Role", e.role], ["Portal user", e.user_email],
            ["PAN", e.pan], ["Bank A/c", e.bank_account_number], ["IFSC", e.ifsc],
            ["Commission Plan", e.commission_applicable ? "Applicable" : "Not applicable"],
            ["Incentive Plan", e.incentive_applicable ? "Applicable" : "Not applicable"],
            ["Monthly Target", inr(e.monthly_target)]].map(([k, v]) => (
            <p key={k}><span className="text-muted-foreground">{k}: </span><b>{v || "—"}</b></p>))}
        </div>
      </Panel>
      <Panel title="Edit employee" testid="edit-employee-panel">
        <QuickForm testid="edit-employee-form" values={edit} submitLabel="Save changes"
                   fields={CONFIG.Employees.fields}
                   onChange={(k, v) => setEdit((f) => ({ ...f, [k]: v }))}
                   onSubmit={async () => {
                     const r = await put(`/crm/employees/${employeeId}`, edit, "Employee updated");
                     if (r) { setEdit({}); detail.reload(); }
                   }} />
      </Panel>
      <Panel title={`Applications (${d.applications.length})`} testid="employee-applications-panel">
        <DataTable testid="employee-applications-table" rows={d.applications}
                   columns={[{ key: "application_id", label: "Application" }, { key: "customer_name", label: "Customer" },
                     { key: "bank_name", label: "Bank" }, { key: "product_name", label: "Product" },
                     { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                     { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
      </Panel>
      <PartyDocuments partyType="employee" partyId={employeeId} />
    </div>
  );
};

export default function Masters() {
  const { user } = useAuth();
  const privileged = ["admin", "finance"].includes(user?.role);
  const [tab, setTab] = useState("Products");
  const [form, setForm] = useState({});
  const [selected, setSelected] = useState(null);
  const cfg = CONFIG[tab];
  const data = useApi(cfg.list, [tab]);

  const startEdit = (row) => {
    const next = { ...row };
    if (!privileged) SENSITIVE.forEach((k) => delete next[k]);
    delete next.kyc_status;
    setForm(next);
  };

  if (selected && tab === "Partners") {
    return <PartnerDetail partnerId={selected} onBack={() => { setSelected(null); data.reload(); }} />;
  }
  if (selected && tab === "Employees") {
    return <EmployeeDetail employeeId={selected} onBack={() => { setSelected(null); data.reload(); }} />;
  }

  const submit = async () => {
    const body = { ...form };
    if (typeof body.required_documents === "string")
      body.required_documents = body.required_documents.split(",").map((s) => s.trim()).filter(Boolean);
    const editing = cfg.idKey && form[cfg.idKey];
    const r = editing
      ? await put(`${cfg.list}/${form[cfg.idKey]}`, body, `${tab.replace(/s$/, "")} updated`)
      : await post(cfg.create, body, `${tab.replace(/s$/, "")} saved`);
    if (r) { setForm({}); data.reload(); }
  };

  return (
    <div className="space-y-6" data-testid="masters-page">
      <PageHead eyebrow="Masters" title="Master Data"
                subtitle="Products, employees, partners with KYC, versioned commission rules and incentive slabs. Sensitive fields are masked server-side unless you are Admin or Finance." />
      <Tabs tabs={Object.keys(CONFIG)} active={tab} onChange={(t) => { setTab(t); setForm({}); setSelected(null); }}
            testid="masters-tabs" />
      <Panel title={cfg.idKey && form[cfg.idKey] ? `Edit ${form[cfg.idKey]}` : `Create ${tab.replace(/s$/, "")}`}
             testid={`create-${cfg.testid}-panel`}
             aside={cfg.idKey && form[cfg.idKey] && (
               <Button size="sm" variant="ghost" onClick={() => setForm({})} data-testid="cancel-edit-btn">
                 Cancel edit</Button>)}>
        <QuickForm fields={cfg.fields} values={form} onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                   testid={`${cfg.testid}-form`}
                   submitLabel={cfg.idKey && form[cfg.idKey] ? "Save changes" : `Save ${tab.replace(/s$/, "")}`}
                   onSubmit={submit} />
      </Panel>
      <Panel title={`${tab} (${(data.data || []).length})`} testid={`${cfg.testid}-panel`}
             aside={cfg.idKey && <span className="text-xs text-muted-foreground">Use Edit / Open on a row</span>}>
        <DataTable testid={`${cfg.testid}-table`} rows={data.data}
                   columns={[...cfg.columns, ...(cfg.idKey ? [{
                     key: "actions", label: "Actions", render: (r) => (
                       <span className="flex gap-1">
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`edit-${r[cfg.idKey]}`}
                                 onClick={() => startEdit(r)}>Edit</Button>
                         {["Partners", "Employees"].includes(tab) && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`open-${r[cfg.idKey]}`}
                                   onClick={() => setSelected(r[cfg.idKey])}>Open</Button>)}
                       </span>),
                   }] : [])]} />
      </Panel>
    </div>
  );
}
